import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Text, View, ScrollView, TouchableOpacity } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useLocalSearchParams, useRouter, useNavigation } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import Avatar from '@/components/People/Avatar';
import Callout from '@/components/General/Callout';
import SolidButton from '@/components/General/SolidButton';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import FieldRenderer from '@/components/Forms/FieldRenderer';
import FormResources from '@/components/Forms/FormResources';
import FormEntryFiles from '@/components/Forms/FormEntryFiles';
import FormEntryPaymentPanel from '@/components/Forms/FormEntryPaymentPanel';
import { paymentStatusLabels } from '@/data/formPayments';
import { convertEntryToPerson, updateEntryLink, deleteFormEntry } from '@/data/forms';
import { showAlert } from '@/utils/showAlert';
import { saveToRecents } from '@/utils/recentlyVisited';
import { flattenFields } from '@/utils/formsSchema';
import { canManageFormEntries } from '@/utils/formPermissions';
import { findEntryUpload } from '@/utils/formEntryNotifications';

export default function FormEntryDetailScreen() {
    const { entryID, source, file: requestedFile } = useLocalSearchParams();
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const [linkPickerOpen, setLinkPickerOpen] = useState(false);

    const { data: loggedInUser } = Query.people.loggedInUser();
    const isMyEntryRoute = source === 'mine';
    const { data: managedEntry, isFetching: managedFetching } = Query.formEntries.byId({ id: entryID, options: { enabled: !!entryID && !isMyEntryRoute } });
    const { data: myEntryRows = [], isFetching: myEntryFetching } = Query.formEntries.mine({ entryID, options: { enabled: !!entryID && isMyEntryRoute } });
    const entry = isMyEntryRoute ? myEntryRows[0] : managedEntry;
    const isFetching = isMyEntryRoute ? myEntryFetching : managedFetching;
    // Resolve only from an entry already authorized by RLS, never from an arbitrary URL path.
    const linkedUpload = useMemo(() => findEntryUpload(entry?.payload, requestedFile), [entry?.payload, requestedFile]);

    // `entry.Form` arrives with schema/resourcePaths already resolved to the
    // submission-time snapshot when one exists (see getFormEntry).
    const form = entry?.Form;
    const schema = form?.schema;
    const fields = useMemo(() => flattenFields(schema), [schema]);
    const sections = useMemo(() => schema?.sections ?? [], [schema]);
    // Repeatable sections that actually have repeat instances in this entry's payload.
    const repeatSections = useMemo(() => {
        const repeats = entry?.payload?.__repeats__;
        if (!repeats || typeof repeats !== 'object') return [];
        return sections.filter((s) => s.allowAddMore && Array.isArray(repeats[s.id]) && repeats[s.id].length > 0);
    }, [sections, entry?.payload?.__repeats__]);

    // Per-form grant: admin, form creator, or a role with canManageEntries
    // ("Manage Entries") on this form. The Form embed carries FormRoles so the
    // check works without an extra query.
    const canManage = !isMyEntryRoute && canManageFormEntries(form, loggedInUser);

    const { data: submittedByPerson } = Query.people.byId({
        id: entry?.submittedByPersonID,
        options: { enabled: canManage && !!entry?.submittedByPersonID },
    });

    useEffect(() => {
        if (entry?.id) {
            saveToRecents({
                id: entry.id,
                url: `/forms/entries/${entry.id}`,
                title: form?.title || 'Form Entry',
                params: { entryID: entry.id },
            }).catch(() => {});
        }
    }, [entry?.id, form?.title]);

    const convertMutation = useMutation({
        mutationFn: () => convertEntryToPerson({ entryID }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.byId(entryID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.byFormId(form?.id) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.mine() });
        },
    });

    const linkMutation = useMutation({
        mutationFn: (personID) => updateEntryLink({ entryID, personID }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.byId(entryID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.byFormId(form?.id) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.mine() });
        },
    });

    const deleteEntryMutation = useMutation({
        mutationFn: () => deleteFormEntry({ entryID }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.byFormId(form?.id) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.mine() });
            router.back();
        },
    });

    const handleConvert = useCallback(() => {
        showAlert({
            title: 'Create Person',
            subtitle: 'Create a new Person record from this entry and link it?',
            confirmText: 'Create',
            onConfirm: () => convertMutation.mutate(),
        });
    }, [convertMutation]);

    const handleLinkSelect = useCallback((selected) => {
        if (selected?.id) {
            linkMutation.mutate(selected.id);
        }
    }, [linkMutation]);

    // Render a single field's read-only value. `keySuffix` disambiguates repeat
    // instances (e.g. ":sec_x:2") so React keys + testIDs stay unique.
    // renderMode="submission" gives signature/profile fields their read-only
    // evidence rendering; other fields behave as before under `disabled`.
    const renderFieldBlock = useCallback((field, value, keySuffix = '') => {
        const isFile = field.type === 'file';
        const key = `${field.id}${keySuffix}`;
        return (
            <View key={key} style={styles.fieldBlock} testID={`entry-field-${field.fieldId}${keySuffix}`}>
                <Text style={styles.fieldLabel}>{field.label || field.fieldId}</Text>
                {isFile ? (
                    <FormEntryFiles value={value} testID={`entry-file-${field.fieldId}${keySuffix}`} />
                ) : (
                    <FieldRenderer
                        field={field.type === 'payment' && entry.paymentSnapshot ? { ...field, config: entry.paymentSnapshot } : field}
                        value={field.type === 'payment' && entry.paymentSnapshot ? entry.payload?.[entry.paymentSnapshot.fieldId] : value}
                        renderMode="submission"
                        formContext={{ formID: form?.id, formIsPublic: !!form?.isPublic }}
                        loggedInUser={loggedInUser}
                    />
                )}
            </View>
        );
    }, [loggedInUser, entry?.paymentSnapshot, entry?.payload, form?.id, form?.isPublic]);

    // Header menu
    useEffect(() => {
        if (!entry) return;
        const options = [];
        if (canManage) {
            options.push({
                name: entry.paymentSnapshot ? 'Archive Entry' : 'Delete Entry',
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: () => {
                    showAlert({
                        title: entry.paymentSnapshot ? 'Archive Entry' : 'Delete Entry',
                        subtitle: entry.paymentSnapshot ? 'Archive this entry? Payment records and accounting history remain intact.' : 'Are you sure you want to delete this form entry? This cannot be undone.',
                        isDestructive: true,
                        confirmText: entry.paymentSnapshot ? 'Archive' : 'Delete',
                        onConfirm: () => deleteEntryMutation.mutate(),
                    });
                },
            });
        }
        navigation.setOptions({
            headerRight: () => options.length > 0 ? <DropdownMenu options={options} /> : null,
        });
    }, [navigation, entry, canManage, deleteEntryMutation]);

    if (isFetching && !entry) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" />
            </View>
        );
    }

    if (!entry) {
        return (
            <View style={styles.loadingContainer}>
                <Text style={styles.emptyText}>Entry not found</Text>
            </View>
        );
    }

    return (
        <ScrollView style={styles.container} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
            <View style={styles.headerCard}>
                <View style={styles.headerRow}>
                    <Unicon name="mail-open-outline" size={24} colorVariant="forms" />
                    <View style={styles.headerMeta}>
                        <Text style={styles.title}>{form?.title || 'Form Entry'}</Text>
                        <Text style={styles.subtitle}>
                            Submitted {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : '—'}
                        </Text>
                    </View>
                </View>
            </View>

            {linkedUpload && (
                <View style={styles.metaCard} testID="entry-linked-upload">
                    <Text style={styles.sectionLabel}>Requested File</Text>
                    <FormEntryFiles value={linkedUpload} testID="entry-linked-upload-file" />
                </View>
            )}
            {!!entry.archivedAt && <Callout type="warning" title="Archived entry" message="Payment history is preserved. This entry cannot accept a new payment." />}
            {entry.paymentSnapshot && (canManage ? <FormEntryPaymentPanel entry={entry} /> : (
                <View>
                    <Callout type="info" title="Payment" message={paymentStatusLabels[entry.paymentStatus] || 'Not Required'} />
                    {!!entry.paymentToken && ['UNPAID', 'PENDING'].includes(entry.paymentStatus) && ['electronic', 'both'].includes(entry.paymentSnapshot?.allowedChannels) && (
                        <SolidButton
                            text="Complete payment"
                            type="forms"
                            onPress={() => router.push({ pathname: '/form-payment', params: { token: entry.paymentToken } })}
                            testID="entry-complete-payment"
                        />
                    )}
                </View>
            ))}
            {entry.isSchemaStale && (
                <View style={styles.calloutWrap}>
                    <Callout
                        type="warning"
                        title="Form has changed"
                        message="This form has been edited since this entry was submitted; some fields may not match."
                    />
                </View>
            )}

            {/* People linking */}
            {canManage && (
            <View style={styles.linkCard}>
                <View style={styles.linkCardHeader}>
                    <Text style={styles.sectionLabel}>Linked Person</Text>
                    {entry.LinkedPerson && (
                        <SolidButton
                            text="Unlink"
                            onPress={() => linkMutation.mutate(null)}
                            processing={linkMutation.isPending}
                            variant="muted"
                            size="sm"
                            type="textSecondary"
                            testID="entry-unlink-person"
                        />
                    )}
                </View>
                {entry.LinkedPerson ? (
                    <TouchableOpacity
                        style={styles.linkedRow}
                        testID="entry-linked-person"
                        onPress={() => router.push('/people/' + entry.LinkedPerson.id)}
                    >
                        <Avatar size={32} profile={entry.LinkedPerson} disableBorder />
                        <Text style={styles.linkedName}>
                            {entry.LinkedPerson.firstName} {entry.LinkedPerson.lastName}
                        </Text>
                        <Unicon name="chevron-forward-outline" size={16} colorVariant="textSecondary" />
                    </TouchableOpacity>
                ) : (
                    <View>
                        <Text style={styles.unlinkedText}>
                            {entry.linkAmbiguous
                                ? 'Multiple possible matches found — link manually.'
                                : 'No person linked to this entry.'}
                        </Text>
                        <View style={styles.linkActions}>
                            <SolidButton
                                text="Link to Person"
                                onPress={() => setLinkPickerOpen(true)}
                                variant="outlined"
                                size="sm"
                                testID="entry-link-person"
                            />
                            <SolidButton
                                text="Create Person from Entry"
                                onPress={handleConvert}
                                processing={convertMutation.isPending}
                                variant="outlined"
                                size="sm"
                                testID="entry-create-person"
                            />
                        </View>
                    </View>
                )}
            </View>
            )}

            {canManage && !entry.LinkedPerson && (
                <SheetSelector
                    title="Link to Person"
                    open={linkPickerOpen}
                    onOpen={() => setLinkPickerOpen(true)}
                    onClose={() => setLinkPickerOpen(false)}
                    onCancel={() => setLinkPickerOpen(false)}
                    type="people"
                    onChange={handleLinkSelect}
                />
            )}

            {/* Submitted by metadata */}
            {(submittedByPerson || entry.schemaVersion != null) && (
                <View style={styles.metaCard}>
                    {submittedByPerson && (
                        <TouchableOpacity
                            style={styles.metaRow}
                            testID="entry-submitted-by"
                            onPress={() => router.push('/people/' + submittedByPerson.id)}
                        >
                            <Unicon name="person-outline" size={16} colorVariant="textSecondary" />
                            <Text style={styles.metaText}>
                                Submitted by {submittedByPerson.firstName} {submittedByPerson.lastName}
                            </Text>
                        </TouchableOpacity>
                    )}
                    <View style={styles.metaRow}>
                        <Unicon name="document-text-outline" size={16} colorVariant="textSecondary" />
                        <Text style={styles.metaText}>
                            Schema version: {entry.schemaVersion}
                            {entry.isSchemaStale ? ' (stale — form edited since)' : ''}
                        </Text>
                    </View>
                </View>
            )}

            {/* Documents attached at submit time (formContentSnapshot.resources
                resolved in getFormEntry — descriptor list is passed straight
                through so no describe call is needed). */}
            <FormResources formID={form?.id} paths={form?.resourcePaths} resources={form?.resources} />

            {/* Payload rendered against the snapshot schema (current schema is
                the legacy fallback for pre-snapshot entries) */}
            <View style={styles.payloadCard} testID="form-entry-view">
                <Text style={styles.sectionLabel}>Responses</Text>
                {fields.length === 0 && (
                    <Text style={styles.emptyText}>No fields defined on this form&apos;s schema.</Text>
                )}
                {fields.map((field) => renderFieldBlock(field, entry.payload?.[field.fieldId]))}

                {/* Repeat ("Add Another") instances — grouped under each section. */}
                {repeatSections.map((section) => {
                    const instances = entry.payload.__repeats__[section.id];
                    const sectionFields = (section.fields || []).filter((f) => !f.hidden);
                    return instances.map((instance, i) => (
                        <View key={`${section.id}-${i}`} style={styles.repeatGroup} testID={`entry-repeat-${section.id}-${i}`}>
                            <Text style={styles.repeatHeader}>
                                {section.title || 'Entry'} {i + 2}
                            </Text>
                            {sectionFields.map((field) =>
                                renderFieldBlock(field, instance?.[field.fieldId], `:${section.id}:${i + 2}`),
                            )}
                        </View>
                    ));
                })}
            </View>
        </ScrollView>
    );
}

const styles = StyleSheet.create((theme) => ({
    container: { flex: 1, backgroundColor: 'transparent' },
    content: { padding: 16, maxWidth: 700, alignSelf: 'center', width: '100%', paddingBottom: 40, gap: 12 },
    loadingContainer: { flex: 1, alignItems: 'center', justifyContent: 'center' },
    emptyText: {
        fontSize: 14,
        fontFamily: theme.fonts.body,
        color: theme.colors.textSecondary,
    },

    headerCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 14,
        padding: 16,
        boxShadow: theme.shadows.card,
    },
    headerRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
    headerMeta: { flex: 1 },
    title: {
        fontSize: 18,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
    subtitle: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },

    calloutWrap: {},

    linkCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 14,
        padding: 16,
        boxShadow: theme.shadows.card,
    },
    sectionLabel: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    linkCardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 10,
    },
    linkedRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    linkedName: {
        flex: 1,
        fontSize: 15,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
    unlinkedText: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        marginBottom: 10,
    },
    linkActions: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },

    metaCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 14,
        padding: 16,
        gap: 8,
        boxShadow: theme.shadows.card,
    },
    metaRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    metaText: {
        fontSize: 13,
        fontFamily: theme.fonts.body,
        color: theme.colors.textSecondary,
    },

    payloadCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 14,
        padding: 16,
        boxShadow: theme.shadows.card,
    },
    fieldBlock: {
        paddingVertical: 6,
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
    },
    fieldLabel: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textTertiary,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        marginBottom: 4,
    },
    repeatGroup: {
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 2,
        borderTopColor: theme.colors.border,
    },
    repeatHeader: {
        fontSize: 13,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        marginBottom: 4,
    },
}));
